<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Motor de planilla (prueba)</h1>
    </div>

    <!-- Crear período -->
    <b-card class="custom-card mb-4">
      <b-card-header class="custom-card-header">
        <h3>Crear periodo</h3>
      </b-card-header>
      <b-card-body class="custom-card-body">
        <div class="row">
          <div class="col-md-3">
            <div class="form-group">
              <label>Empresa *</label>
              <FormSelect id="companyId" v-model="form.companyId" :items="companies" defaultOption="Elegir empresa" />
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label>Tipo de planilla *</label>
              <FormSelect id="payrollTypeId" v-model="form.payrollTypeId" :items="payrollTypes" defaultOption="Elegir tipo" />
            </div>
          </div>
          <div class="col-md-1">
            <div class="form-group">
              <label>Año *</label>
              <input type="number" v-model.number="form.periodYear" class="custom-form-control" />
            </div>
          </div>
          <div class="col-md-1">
            <div class="form-group">
              <label>Mes *</label>
              <input type="number" v-model.number="form.periodMonth" min="1" max="12" class="custom-form-control" />
            </div>
          </div>
          <div class="col-md-2" v-if="selectedFrequency === 'WEEKLY'">
            <div class="form-group">
              <label>Semana *</label>
              <FormSelect id="periodWeek" v-model="form.periodWeek" :items="weekOptions" defaultOption="Seleccione" />
            </div>
          </div>
          <div class="col-md-2" v-if="selectedFrequency === 'BIWEEKLY'">
            <div class="form-group">
              <label>Quincena *</label>
              <FormSelect id="periodHalf" v-model="form.periodHalf" :items="halfOptions" defaultOption="Seleccione" />
            </div>
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <div class="form-group w-100">
              <button class="button button-primary w-100" @click="createPeriod"
                :disabled="!canCreate || creating">
                {{ creating ? 'Creando...' : 'Crear periodo' }}
              </button>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Planilla creada -->
    <b-card v-if="payroll" class="custom-card mb-4">
      <b-card-header class="custom-card-header d-flex justify-content-between align-items-center">
        <h3>Planilla #{{ payroll.id }}</h3>
        <span :class="`badge ${statusBadge}`">{{ payroll.payrollStatus }}</span>
      </b-card-header>
      <b-card-body class="custom-card-body">
        <div class="row mb-3">
          <div class="col-md-3">
            <small class="text-muted d-block">Tipo</small>
            <strong>{{ payroll.payrollTypeName }}</strong>
          </div>
          <div class="col-md-3">
            <small class="text-muted d-block">Empresa</small>
            <strong>{{ payroll.companyName }}</strong>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">Periodo</small>
            <strong>{{ payroll.periodYear }}-{{ String(payroll.periodMonth).padStart(2, '0') }}</strong>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">Empleados</small>
            <strong>{{ payroll.totalEmployees }}</strong>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">Estado</small>
            <strong>{{ payroll.payrollStatus }}</strong>
          </div>
        </div>

        <!-- Totales (solo si calculado) -->
        <div class="row mb-3" v-if="payroll.payrollStatus !== 'DRAFT'">
          <div class="col-md-3">
            <small class="text-muted d-block">Total bruto</small>
            <strong class="text-success">S/ {{ formatNumber(payroll.totalGross) }}</strong>
          </div>
          <div class="col-md-3">
            <small class="text-muted d-block">Total neto</small>
            <strong class="text-primary">S/ {{ formatNumber(payroll.totalNet) }}</strong>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">Aportes (AFP/ONP/EsSalud)</small>
            <strong class="text-danger">S/ {{ formatNumber(payroll.totalContributions) }}</strong>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">IR 5ta categoria</small>
            <strong class="text-warning">S/ {{ formatNumber(payroll.totalIr) }}</strong>
          </div>
          <div class="col-md-2">
            <small class="text-muted d-block">Calculado por</small>
            <strong>{{ payroll.calculatedBy || '---' }}</strong>
          </div>
        </div>

        <div class="d-flex">
          <button class="button button-primary mr-2" @click="calculate"
            v-if="payroll.payrollStatus === 'DRAFT' || payroll.payrollStatus === 'CALCULATED'"
            :disabled="calculating">
            {{ calculating ? 'Calculando...' : (payroll.payrollStatus === 'CALCULATED' ? 'Recalcular' : 'Calcular') }}
          </button>
          <button class="button button-primary mr-2" @click="approve"
            v-if="payroll.payrollStatus === 'CALCULATED'">
            Aprobar
          </button>
          <button class="button button-primary mr-2" @click="markAsPaid"
            v-if="payroll.payrollStatus === 'APPROVED'">
            Marcar como pagado
          </button>
          <button class="button button-light mr-2" @click="annul"
            v-if="payroll.payrollStatus !== 'PAID' && payroll.payrollStatus !== 'ANNULLED'">
            Anular
          </button>
          <button class="button button-light" @click="exportExcel"
            v-if="payroll.payrollStatus !== 'DRAFT'"
            :disabled="exporting">
            <i class="ri-file-excel-line mr-1"></i>{{ exporting ? 'Exportando...' : 'Exportar Excel' }}
          </button>
        </div>
      </b-card-body>
    </b-card>

    <!-- Empleados excluidos por documentación incompleta -->
    <b-card v-if="payroll && payroll.excludedEmployees && payroll.excludedEmployees.length" class="custom-card mb-4">
      <b-card-header class="custom-card-header">
        <h3 class="text-danger">
          <i class="ri-lock-line mr-2"></i>
          Excluidos por documentación incompleta ({{ payroll.excludedEmployees.length }})
        </h3>
      </b-card-header>
      <b-card-body class="custom-card-body p-0">
        <div class="table-responsive">
          <table class="custom-table w-100">
            <thead>
              <tr>
                <th>COLABORADOR</th>
                <th>DNI</th>
                <th>DOCUMENTOS FALTANTES</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="exc in payroll.excludedEmployees" :key="exc.employeeId">
                <td class="pl-3">{{ exc.employeeName }}</td>
                <td>{{ exc.identifier }}</td>
                <td class="text-danger">{{ exc.missingDocuments ? exc.missingDocuments.join(', ') : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card-body>
    </b-card>

    <!-- Detalle por empleado -->
    <b-card v-if="details.length > 0" class="custom-card">
      <b-card-header class="custom-card-header">
        <h3>Detalle por empleado ({{ details.length }})</h3>
      </b-card-header>
      <b-card-body class="custom-card-body p-0">
        <div class="table-responsive">
          <table class="custom-table w-100">
            <thead>
              <tr>
                <th>EMPLEADO</th>
                <th>DOCUMENTO</th>
                <th class="text-right">SUELDO BASE</th>
                <th class="text-right">ASIG. FAM.</th>
                <th class="text-right">BRUTO</th>
                <th>PENSION</th>
                <th class="text-right">AFP/ONP</th>
                <th class="text-right">IR 5ta</th>
                <th class="text-right">ESSALUD</th>
                <th class="text-right">NETO</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in details" :key="d.id">
                <td class="pl-3">{{ d.employeeName }}</td>
                <td>{{ d.employeeDocumentNumber }}</td>
                <td class="text-right">{{ formatNumber(d.baseSalary) }}</td>
                <td class="text-right">{{ formatNumber(d.familyAllowance) }}</td>
                <td class="text-right font-weight-bold">{{ formatNumber(d.grossSalary) }}</td>
                <td>
                  <span class="badge badge-secondary">{{ d.pensionSystem || '-' }}</span>
                  <small v-if="d.pensionEntityName && d.pensionSystem === 'AFP'" class="ml-1">{{ d.pensionEntityName }}</small>
                </td>
                <td class="text-right text-danger">{{ formatNumber(d.totalAfp > 0 ? d.totalAfp : d.onpAmount) }}</td>
                <td class="text-right text-warning">{{ formatNumber(d.irRetention) }}</td>
                <td class="text-right text-muted">{{ formatNumber(d.essaludAmount) }}</td>
                <td class="text-right font-weight-bold text-primary">{{ formatNumber(d.netSalary) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-weight-bold" style="background: #f8f9fa;">
                <td class="pl-3" colspan="2">TOTALES</td>
                <td class="text-right">{{ formatNumber(sumField('baseSalary')) }}</td>
                <td class="text-right">{{ formatNumber(sumField('familyAllowance')) }}</td>
                <td class="text-right">{{ formatNumber(sumField('grossSalary')) }}</td>
                <td></td>
                <td class="text-right text-danger">{{ formatNumber(sumPension()) }}</td>
                <td class="text-right text-warning">{{ formatNumber(sumField('irRetention')) }}</td>
                <td class="text-right text-muted">{{ formatNumber(sumField('essaludAmount')) }}</td>
                <td class="text-right text-primary">{{ formatNumber(sumField('netSalary')) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </b-card-body>
    </b-card>

  </div>
</template>

<script>
import { Alert, FormSelect } from 'wize-admin'
import fileDownload from 'js-file-download'
import { CompanyService, PayrollTypeService, PayrollEngineService } from '@/core/services'

export default {
  components: { FormSelect },
  data() {
    return {
      companies: [],
      payrollTypes: [],
      form: {
        companyId: null,
        payrollTypeId: null,
        periodYear: 2026,
        periodMonth: new Date().getMonth() + 1,
        periodWeek: null,
        periodHalf: null,
      },
      weekOptions: [
        { key: 1, label: 'Semana 1 (dias 1-7)' },
        { key: 2, label: 'Semana 2 (dias 8-14)' },
        { key: 3, label: 'Semana 3 (dias 15-21)' },
        { key: 4, label: 'Semana 4 (dias 22-fin)' },
      ],
      halfOptions: [
        { key: 1, label: '1ra quincena (dias 1-15)' },
        { key: 2, label: '2da quincena (dias 16-fin)' },
      ],
      payroll: null,
      details: [],
      creating: false,
      calculating: false,
      exporting: false,
    }
  },
  watch: {
    'form.payrollTypeId'() {
      this.form.periodWeek = null
      this.form.periodHalf = null
    },
  },
  computed: {
    selectedFrequency() {
      if (!this.form.payrollTypeId) return null
      const pt = this.payrollTypes.find(t => t.key === this.form.payrollTypeId)
      return pt ? pt.frequency : null
    },
    canCreate() {
      if (!this.form.companyId || !this.form.payrollTypeId) return false
      if (this.selectedFrequency === 'WEEKLY' && !this.form.periodWeek) return false
      if (this.selectedFrequency === 'BIWEEKLY' && !this.form.periodHalf) return false
      return true
    },
    statusBadge() {
      const map = {
        DRAFT: 'badge-secondary',
        CALCULATED: 'badge-info',
        APPROVED: 'badge-green',
        PAID: 'badge-success',
        ANNULLED: 'badge-danger',
      }
      return map[this.payroll?.payrollStatus] || 'badge-gray'
    },
  },
  methods: {
    formatNumber(val) {
      if (val == null) return '0.00'
      return Number(val).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    sumField(field) {
      return this.details.reduce((sum, d) => sum + (Number(d[field]) || 0), 0)
    },
    sumPension() {
      return this.details.reduce((sum, d) => sum + (Number(d.totalAfp) || 0) + (Number(d.onpAmount) || 0), 0)
    },
    async createPeriod() {
      this.creating = true
      try {
        const resp = await PayrollEngineService.createPeriod(this.form)
        this.payroll = resp.payload
        this.details = []
        Alert.success(`Periodo creado con ${this.payroll.totalEmployees} empleados`)
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al crear periodo')
      } finally {
        this.creating = false
      }
    },
    async calculate() {
      this.calculating = true
      try {
        const action = this.payroll.payrollStatus === 'CALCULATED' ? 'recalculate' : 'calculate'
        const resp = action === 'recalculate'
          ? await PayrollEngineService.recalculate(this.payroll.id)
          : await PayrollEngineService.calculate(this.payroll.id)
        this.payroll = resp.payload
        await this.loadDetails()
        Alert.success('Planilla calculada correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al calcular')
      } finally {
        this.calculating = false
      }
    },
    async approve() {
      try {
        const resp = await PayrollEngineService.approve(this.payroll.id)
        this.payroll = resp.payload
        Alert.success('Planilla aprobada')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al aprobar')
      }
    },
    async markAsPaid() {
      try {
        const today = new Date().toISOString().substring(0, 10)
        const resp = await PayrollEngineService.pay(this.payroll.id, today)
        this.payroll = resp.payload
        Alert.success('Planilla marcada como pagada')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al registrar pago')
      }
    },
    async annul() {
      try {
        const resp = await PayrollEngineService.annul(this.payroll.id)
        this.payroll = resp.payload
        this.details = []
        Alert.success('Planilla anulada')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al anular')
      }
    },
    async loadDetails() {
      try {
        const resp = await PayrollEngineService.getDetails(this.payroll.id)
        this.details = resp.payload || []
      } catch (ex) {
        console.error(ex)
      }
    },
    async exportExcel() {
      this.exporting = true
      try {
        const resp = await PayrollEngineService.exportExcel(this.payroll.id)
        const fileName = `planilla_${this.payroll.payrollTypeCode || 'PL'}_${this.payroll.periodYear}-${String(this.payroll.periodMonth).padStart(2, '0')}.xlsx`
        fileDownload(resp, fileName)
      } catch (ex) {
        console.error(ex)
        Alert.error('Error al exportar Excel')
      } finally {
        this.exporting = false
      }
    },
  },
  async created() {
    try {
      const [companiesResp, typesResp] = await Promise.all([
        CompanyService.listAll(),
        PayrollTypeService.listActive(),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.payrollTypes = (typesResp.payload || []).map(t => ({ key: t.id, label: t.name, frequency: t.frequency }))
    } catch (ex) {
      console.error(ex)
      Alert.error('Error al cargar catalogos')
    }
  },
}
</script>
